/**
 * 배역 아바타.
 *
 * **모델은 나중에 꽂는다.** 지금은 절차적으로 세우고, `MODELS` 에 파일 경로만
 * 넣으면 그 자리에서 통째로 갈린다. 그래서 이 파일 밖에서는 아무도 아바타의
 * 속을 모른다 — `buildAvatar(castId, color)` 하나만 부른다.
 *
 * 그리고 여기 있는 것은 **남이 보는 것**뿐이다.
 *   왼손 검지 끝 마디가 없다        <- 손가락을 짧게 만든다
 *   안경을 자주 닦는다 · 시계가 없다  <- 안경을 씌우고 손목을 비운다
 *   어깨와 무릎에 눈이 녹은 자국      <- 흰 얼룩을 얹는다
 * **왜 그런지는 그 사람 대본에만 있다.** 아바타는 절대 이유를 말하지 않는다.
 */

import * as THREE from 'three';
import { ep as epData } from './episodeData';
import { CAST_BY_ID } from '../../shared/cast.gen';
import type { Skin as Skin2 } from './motion';

/**
 * 배역별 3D 모델. 넣으면 절차적 아바타 대신 이 모델이 선다.
 *
 * **모델을 받으면 여기만 채운다.** 나머지 코드는 안 건드린다.
 *
 *   형식   .glb (gltf 2.0). 한 배역 한 파일
 *   축     +Y 위, **-Z 가 정면**. 발바닥이 y=0
 *   크기   실제 키 그대로 미터 단위 (송이경 1.63, 정우빈 1.78 ...)
 *          BODIES 의 height 와 어긋나면 여기서 맞춰 스케일한다
 *   본     머리 본 이름을 `head` 로 두면 시선이 따라 돈다. 없으면 몸통째 돈다
 *   면수   한 몸에 8k 삼각형 아래. 여섯이 동시에 서고 그림자를 진다
 *   두는 곳 client/public/models/<배역>.glb  ->  경로는 '/models/song.glb'
 *
 * 얼굴에 그려 넣어야 하는 것 (남이 보는 것이라 모델에 있어야 한다):
 *   명주원  왼손 검지 끝 마디가 없다
 *   배정호  안경 · 손목에 시계 자국만 있고 시계가 없다
 *   정우빈  어깨와 무릎에 눈이 녹은 자국
 *   차유리  사흘째 같은 옷 · 손톱을 물어뜯은 자국
 *   윤가람  손이 트고 갈라져 있다 · 도예 앞치마
 *   송이경  눈 밑이 검다 · 화장기 없음
 * **왜 그런지는 절대 모델에 넣지 않는다.** 그건 그 사람 대본에만 있다.
 */
export const MODELS: Record<string, string | null> = {
  // 배역 id 가 사건마다 다르므로 한 표에 같이 둔다 — id 만 보고 모델을 찾는다
  // --- 사흘 ---
  jung: '/models/jung.fbx',
  myeong: '/models/myeong.fbx',
  yuri: '/models/cha.glb',
  bae: '/models/bae.fbx',
  yun: '/models/yoon.glb',
  song: '/models/song.glb',
  /** 피해자. 시신으로 세울 때 쓴다 */
  han: '/models/han.glb',
};

/**
 * **텍스처 덮어쓰기.** 리깅은 그대로 두고 알베도·노멀맵만 더 좋은 것으로 바꾼다.
 *
 * 모델을 다시 뽑으면 리그가 날아가고 Blender 작업을 또 해야 한다. 그런데 품질을 올리는
 * 것은 대개 텍스처다 — 받은 모델은 **알베도 1024 한 장**이 전부고 노멀맵이 없다.
 * UV 가 같으면 텍스처만 바꿔도 그대로 맞는다. 같은지는 `tools/glb-tex.py cmp` 가 판정한다.
 *
 * 넣는 순서: Tripo 에서 2K 로 다시 내보낸다 -> `tools/glb-tex.py cmp` 로 UV 확인 ->
 * `dump` 로 이미지 추출 -> WebP 로 변환(1.5MB PNG 가 74KB 가 된다) -> 여기 경로만 적는다.
 */
export const SKINS: Record<string, Skin2> = {
  // 2048x2048 알베도. 원본 PNG 34.5MB -> WebP 1.2MB (29배) 로 줄여 넣었다 —
  // 모델에 박혀 있는 1024 보다도 가볍다.
  //
  // **flipY 는 모델 형식에 따라 다르다.** 같은 메시인데도 glTF 와 FBX 는 UV 의 V 축이
  // 서로 뒤집혀 있다(tools/uv-flip.mjs 로 측정: 뒤집으면 100% 일치). 새 이미지는 glTF
  // 방향이라(픽셀 RMSE 0.018 vs 뒤집기 0.239) glTF 모델은 그대로, FBX 모델은 뒤집는다.
  //
  // 노멀맵은 Tripo 내보내기에 **없었다**(알베도 한 장뿐). 있으면 여기 normal 에 넣는다.
  yuri: { albedo: '/models/tex/yuri-2k.webp', flipY: false },
  yun: { albedo: '/models/tex/yun-2k.webp', flipY: false },
  song: { albedo: '/models/tex/song-2k.webp', flipY: false },
  bae: { albedo: '/models/tex/bae-2k.webp', flipY: true },
  jung: { albedo: '/models/tex/jung-2k.webp', flipY: true },
  myeong: { albedo: '/models/tex/myeong-2k.webp', flipY: true },
};


/**
 * 몸. 시나리오에 박힌 수치가 둘 있다 — **한서진 168, 송이경 163.**
 * 「계단참에서 마주친 사람이 키가 좀 작아 보였다」가 그 차이에 걸려 있으므로
 * 이 둘은 임의로 못 바꾼다. 나머지는 저자 판단이다.
 */
interface BodyDef {
  /** cm */
  height: number;
  /** 어깨 폭 배율 */
  build: number;
  coat: string;
  pants: string;
  /** 안경 */
  glasses?: boolean;
  /** 왼손 검지 끝 마디가 없다 */
  shortFinger?: 'L' | 'R';
  /** 어깨와 무릎에 눈이 녹은 자국 */
  snow?: boolean;
  /** 앞치마 */
  apron?: string;
}

const BODIES: Record<string, BodyDef> = {
  jung: { height: 178, build: 1.08, coat: '#1e2a3c', pants: '#161b23', snow: true },
  myeong: { height: 172, build: 1.12, coat: '#4a4436', pants: '#2c2a24', shortFinger: 'L' },
  yuri: { height: 165, build: 0.94, coat: '#6b5a52', pants: '#33302e' },
  bae: { height: 176, build: 0.98, coat: '#2b3138', pants: '#22262b', glasses: true },
  yun: { height: 170, build: 1.02, coat: '#5a4a38', pants: '#2e2820', apron: '#6f5b41' },
  song: { height: 163, build: 0.88, coat: '#1c1c20', pants: '#18181b' },
};

/** 기준 키. 이 키에서 아래 치수가 그대로 나온다 */
const BASE_H = 172;

export interface AvatarParts {
  group: THREE.Group;
  /**
   * **모델이 실제로 붙었는가.** 붙었으면 true, 모델이 없거나 로드가 깨지면 false.
   *
   * 부르는 쪽이 「스킨 메시가 생겼나」를 직접 훑어보고 있었는데, 모델은 이 함수가
   * 반환된 **뒤에** 도착하므로 그 검사는 언제나 false 였다. 시신이 도형으로 남고
   * 그 위에 모델이 겹쳐 선 것이 그래서다. 추측하지 말고 여기서 알려 준다.
   */
  ready: Promise<boolean>;
  /** 시선 방향으로 도는 부분 (절차 아바타용. 모델이 붙으면 머리 **뼈**가 대신 돈다) */
  head: THREE.Group;
  /** 손에 든 물건 */
  held: THREE.Mesh;
  /**
   * 매 프레임 호출. 아바타가 자기 애니메이션을 소유한다.
   *
   * 예전에는 밖에서 `a.head.rotation.x` 와 `a.group.scale.y` 를 직접 만졌다. 모델이 붙으면
   * 그 두 손잡이가 통하지 않는다 — 머리는 스켈레톤 안의 뼈고, 웅크림은 몸통을 눌러 찌그러뜨리는
   * 것이 아니라 무릎을 접는 것이다. 그래서 상태를 넘겨주고 안에서 처리한다.
   *
   * @param speed 수평 이동 속도 (m/s). 걷기/달리기/대기를 섞는 기준
   */
  update: (dt: number, speed: number, pitch: number, crouch: boolean) => void;
  /**
   * **시신으로 굳힌다.** 누운 클립을 한 프레임만 적용하고 바닥에 내려놓은 뒤 멈춘다.
   *
   * 모델이 아직 안 왔어도 불러도 된다 — 도착하면 그때 적용한다.
   * @param floorY 바닥의 월드 Y. 여기에 몸의 최저점을 맞춘다
   * @param headDir 머리가 향할 월드 방향(수평). 주면 그 방향으로 눕힌다 —
   *   안 주면 클립이 정한 방향 그대로이고, 그 경우 orient() 의 «세우는 회전» 때문에
   *   시신이 서 버릴 수 있다
   */
  freezeDead: (floorY?: number, headDir?: THREE.Vector3, style?: DeadStyle) => void;
  /** 관절 포즈를 바꿔 다시 눕힌다 (디버그 슬라이더가 부른다) */
  setPose: (pose: import('./motion').Pose) => void;
  /** UAL 클립을 시신 자세로 (이름, 0~1 어느 프레임) */
  setDeadClip: (name: string, at?: number) => Promise<boolean>;
  /** 포저용 — 모델과 바인드 회전 */
  model: () => THREE.Object3D | null;
  rest: () => import('./motion').RestPose | null;
  getPose: () => import('./motion').Pose;
  /** 진단 — 어떤 클립이 붙었고 시신 클립이 있는지 */
  debug: () => { hasDead: boolean; clips: string[]; ready: boolean };
}

/**
 * 배역별 모델 캐시. 같은 배역이 둘일 수는 없지만, 다시 붙을 때(재접속) 또 내려받지 않는다.
 * 로드 실패는 캐시하지 않는다 — 일시적 실패라면 다음에 다시 해 봐야 한다.
 */
const modelCache = new Map<string, Promise<{ root: THREE.Object3D; clips: THREE.AnimationClip[] }>>();

function loadModel(path: string): Promise<{ root: THREE.Object3D; clips: THREE.AnimationClip[] }> {
  const hit = modelCache.get(path);
  if (hit) return hit;
  // 로더는 **필요할 때 받아온다.** 정적 import 하면 FBX 로더(파서가 크다)가 로비 번들에
  // 들어가 첫 화면이 늦어진다 — 아바타는 게임에 들어가야 필요하다.
  const p = (async () => {
    if (path.endsWith('.fbx')) {
      const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js');
      const obj = await new FBXLoader().loadAsync(path);
      return { root: obj as THREE.Object3D, clips: obj.animations ?? [] };
    }
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const g = await new GLTFLoader().loadAsync(path);
    return { root: g.scene as THREE.Object3D, clips: g.animations ?? [] };
  })();
  p.catch(() => modelCache.delete(path));
  modelCache.set(path, p);
  return p;
}

export type DeadStyle = { curl?: boolean; roll?: number; pose?: 'dead' | 'crouch'; joints?: import('./motion').Pose; /** UAL 클립 이름 — 마지막 프레임을 시신 자세로 */ clip?: string; clipAt?: number };

export function buildAvatar(castId: string | null, color: THREE.Color): AvatarParts {
  const group = new THREE.Group();
  const body = (castId && BODIES[castId]) || {
    height: BASE_H,
    build: 1,
    coat: `#${color.getHexString()}`,
    pants: `#${color.clone().multiplyScalar(0.45).getHexString()}`,
  };
  /**
   * 몸 크기.
   *
   * 배역별 키(`body.height`)에 **사건 비율**을 곱한다. 눈높이를 낮췄으면 몸도
   * 같이 줄어야 한다 — 안 그러면 내 시점은 낮은데 남이 보는 내 몸은 그대로라,
   * 3인칭으로 돌리는 순간 목이 가슴에 달린 사람이 된다.
   *
   * 기준은 사흘의 1.62 다. 초치성(1.45)이면 0.9 배가 된다.
   */
  const s = (body.height / BASE_H) * (epData().eyeHeight / 1.62);

  const coat = new THREE.MeshStandardMaterial({ color: body.coat, roughness: 0.82 });
  const pants = new THREE.MeshStandardMaterial({ color: body.pants, roughness: 0.88 });
  const skin = new THREE.MeshStandardMaterial({ color: '#d3b195', roughness: 0.78 });

  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.25 * body.build, 0.66, 6, 14), coat);
  torso.position.y = 0.76 * s;
  torso.castShadow = true;
  group.add(torso);

  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.105, 0.42, 4, 8), pants);
    leg.position.set(side * 0.12, 0.3 * s, 0);
    leg.castShadow = true;
    group.add(leg);

    // 팔. 손끝은 따로 세워서 손가락을 손볼 수 있게 한다
    const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.072, 0.4, 4, 8), coat);
    arm.position.set(side * (0.3 * body.build), 0.82 * s, 0.02);
    arm.castShadow = true;
    group.add(arm);

    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.062, 8, 6), skin);
    hand.position.set(side * (0.3 * body.build), 0.6 * s, 0.02);
    group.add(hand);

    // 「왼손 검지 끝 마디가 없다」 — 손가락 하나가 눈에 띄게 짧다
    const isThatHand = body.shortFinger === (side < 0 ? 'L' : 'R');
    for (let f = 0; f < 3; f++) {
      const short = isThatHand && f === 1;
      const fin = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.014, short ? 0.026 : 0.062, 3, 5),
        skin,
      );
      fin.position.set(
        side * (0.3 * body.build) + (f - 1) * 0.03,
        0.6 * s - 0.075 - (short ? 0.018 : 0),
        0.02,
      );
      group.add(fin);
    }
  }

  if (body.apron) {
    const ap = new THREE.Mesh(
      new THREE.BoxGeometry(0.42 * body.build, 0.6, 0.06),
      new THREE.MeshStandardMaterial({ color: body.apron, roughness: 0.95 }),
    );
    ap.position.set(0, 0.72 * s, 0.2);
    group.add(ap);
  }

  // 「어깨와 무릎에 눈이 녹은 자국」
  if (body.snow) {
    const wet = new THREE.MeshStandardMaterial({ color: '#8fa3b8', roughness: 0.4 });
    for (const [y, r] of [
      [1.06 * s, 0.27 * body.build],
      [0.42 * s, 0.13],
    ] as const) {
      const patch = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 6), wet);
      patch.scale.set(1, 0.22, 1);
      patch.position.set(0, y, 0);
      group.add(patch);
    }
  }

  const head = new THREE.Group();
  head.position.y = 1.40 * s;
  group.add(head);

  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.148, 18, 14), skin);
  skull.castShadow = true;
  head.add(skull);

  // 얼굴이 어디를 보는지 알아야 한다
  // 눈. 크게 만들면 안대를 찬 것처럼 보인다
  const eye = new THREE.MeshStandardMaterial({ color: '#2a2f38', roughness: 0.45 });
  for (const dx of [-0.052, 0.052]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.019, 8, 6), eye);
    e.position.set(dx, 0.018, -0.132);
    head.add(e);
  }
  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(0.152, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.58),
    new THREE.MeshStandardMaterial({ color: '#241f1c', roughness: 0.9 }),
  );
  hair.position.y = 0.012;
  head.add(hair);

  if (body.glasses) {
    const rim = new THREE.MeshStandardMaterial({ color: '#c9ced6', roughness: 0.3, metalness: 0.6 });
    for (const dx of [-0.052, 0.052]) {
      const lens = new THREE.Mesh(new THREE.TorusGeometry(0.044, 0.007, 6, 14), rim);
      lens.position.set(dx, 0.015, -0.128);
      head.add(lens);
    }
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.042, 0.007, 0.007), rim);
    bridge.position.set(0, 0.015, -0.132);
    head.add(bridge);
  }

  const held = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 0.08, 0.08),
    new THREE.MeshStandardMaterial({ color: '#d9a441', roughness: 0.35, metalness: 0.6 }),
  );
  held.position.set(0.3, 0.95 * s, -0.16);
  held.visible = false;
  group.add(held);

  // **절차 부품 목록을 여기서 찍어 둔다.** 모델이 도착하면 이것들만 숨긴다 — 라벨 스프라이트와
  // 손전등은 remote.ts 가 나중에 붙이므로 목록에 없고, 든 물건은 모델에서도 써야 하므로 뺀다.
  const procKids = group.children.filter((c) => c !== held);

  // --- 모델 (있으면 절차 아바타를 대신한다) ---
  let mixer: THREE.AnimationMixer | null = null;
  type Acts = Record<'idle' | 'walk' | 'run' | 'crouch', THREE.AnimationAction>;
  let acts: Acts | null = null;
  let headBone: THREE.Bone | null = null;
  // 시신 상태. **모델보다 먼저 선언해야 한다** — 로드 완료 콜백이 이 변수들을 쓴다
  let deadAct: THREE.AnimationAction | null = null;
  let wantDead: number | null = null;
  let applyDead: ((floorY: number) => void) | null = null;
  let deadHeadDir: THREE.Vector3 | null = null;
  let dbgClips: string[] = []; let dbgReady = false;
  let restQ: import('./motion').RestPose | null = null;
  let extAct: THREE.AnimationAction | null = null;
  let modelRef: THREE.Object3D | null = null;
  /** UAL 클립을 받아 시신 자세로 쓴다. 관절 포즈보다 우선 */
  const setDeadClip = async (name: string, at = 1) => {
    if (!modelRef || !mixer) return false;
    const motion = await import('./motion');
    const clip = await motion.loadUalClip(modelRef, name);
    if (!clip) return false;
    if (extAct) { extAct.stop(); mixer.uncacheClip(extAct.getClip()); }
    extAct = mixer.clipAction(clip); extAct.play(); extAct.setEffectiveWeight(0);
    deadStyle = { ...deadStyle, joints: undefined, pose: undefined, clip: name, clipAt: at };
    if (wantDead !== null) applyDead?.(wantDead);
    return true;
  };
  let curPose: import('./motion').Pose = {};
  const setPose = (pose: import('./motion').Pose) => { deadStyle = { ...deadStyle, joints: pose }; if (wantDead !== null) applyDead?.(wantDead); };
  const getPose = () => curPose;
  const debug = () => ({ hasDead: !!deadAct, clips: dbgClips, ready: dbgReady });
  let deadStyle: DeadStyle = {};
  const freezeDead = (floorY = 0, headDir?: THREE.Vector3, style?: DeadStyle) => {
    wantDead = floorY;
    deadHeadDir = headDir ?? null;
    deadStyle = style ?? {};
    applyDead?.(floorY);
  };
  const path = castId ? MODELS[castId] : null;
  let settle: (ok: boolean) => void = () => {};
  const ready = new Promise<boolean>((res) => {
    settle = res;
  });
  if (!path) settle(false);
  if (path) {
    void (async () => {
      const [{ root }, { clone }, motion] = await Promise.all([
        loadModel(path),
        import('three/examples/jsm/utils/SkeletonUtils.js'),
        import('./motion'),
      ]);
      const clips = await motion.loadClips();
      dbgClips = Object.keys(clips);
      // 같은 리소스를 여러 아바타가 쓸 수 있으므로 **스켈레톤째** 복제한다. 평범한 clone()
      // 은 뼈를 공유해서 한 사람이 걸으면 다른 사람도 같이 걷는다
      const model = clone(root);
      motion.fixMaterials(model);
      // 관절 포저의 출발점 — 클립을 얹기 **전** 바인드 자세
      restQ = motion.captureRest(model);
      modelRef = model;

      // 회전은 **감싸는 그룹**에 건다 — 모델 자신의 변환에는 로더가 넣어 둔 단위·축 변환이
      // 들어 있다(glTF 의 Armature 는 0.01 배 + X축 90도). `model.rotation.y = ...` 로 오일러를
      // 만지면 그것을 덮어써서 사람이 눕는다. 방향은 아래에서 뼈를 읽어 자동으로 맞춘다
      const holder = new THREE.Group();
      holder.add(model);

      mixer = new THREE.AnimationMixer(model);
      const A = (c: THREE.AnimationClip | undefined) => {
        if (!c) return null;
        const a = mixer!.clipAction(c);
        a.play();
        a.setEffectiveWeight(0);
        return a;
      };
      const idle = A(clips.idle) ?? A(clips.walk);
      if (!idle) throw new Error('클립이 하나도 없다');

      // **키를 재기 전에 포즈를 적용한다.** rest 포즈의 스켈레톤 크기는 클립을 적용한 포즈와
      // 다르다(FBX 변환 모델에서 특히). rest 로 재서 맞추면 키가 어긋난다 —
      // legend-of-fist 가 같은 데서 걸렸다.
      idle.setEffectiveWeight(1);
      mixer.update(0);
      // updateWorldMatrix 가 아니라 **updateMatrixWorld(재귀)** 다. SkinnedMesh 는 앞의 것을
      // 오버라이드하지 않아 bindMatrixInverse 가 갱신되지 않는다
      model.updateMatrixWorld(true);

      // **세우고 정면을 -Z 로 돌린다.** 받은 GLB 는 glTF 공간에서 누워 있었다 —
      // 상수로 박지 않고 뼈에서 읽는다(motion.orient)
      motion.orient(holder, model);

      // 모델은 AI 로 만든 것이라 키가 제멋대로다. 그런데 시나리오의 「계단참에서 마주친
      // 사람이 키가 좀 작아 보였다」가 **한서진 168 · 송이경 163** 의 차이에 걸려 있다 —
      // 모델 크기를 그대로 쓰면 그 단서가 거짓이 된다.
      // 재는 방법은 motion.fitHeight 에 있다 — Box3 는 스킨 메시에서 거짓말을 한다
      motion.fitHeight(model, body.height / 100, holder);

      group.add(holder);
      for (const k of procKids) k.visible = false;

      // 텍스처 덮어쓰기가 선언돼 있으면 얹는다. 실패해도 원래 텍스처로 서 있으면 된다
      const skin = castId ? SKINS[castId] : null;
      if (skin) {
        void motion.applySkin(model, skin).catch((e) =>
          console.warn('[아바타] 텍스처 교체 실패 — 원본으로 남는다:', castId, e),
        );
      }

      headBone = motion.findHead(model);
      acts = {
        idle,
        walk: A(clips.walk) ?? idle,
        run: A(clips.run) ?? A(clips.walk) ?? idle,
        crouch: A(clips.crouch) ?? idle,
      };
      acts.idle.setEffectiveWeight(1);
      deadAct = A(clips.dead);
      applyDead = (floorY: number) => {
        if (!mixer || !acts) return;
        for (const a of Object.values(acts)) a.setEffectiveWeight(0);
        // **웅크린 시신은 crouch 클립을 눕힌다.** 손으로 뼈를 접은 자세(curl)는 무릎·고관절이
        // 해부학적으로 틀린 방향으로 꺾였다(Chris 09-07 「관절이 왜 저럼」). 크라우치는 사람이
        // 실제로 취하는 자세라 관절이 맞고, 옆으로 눕히면 웅크린 몸이 된다
        const base = deadStyle.pose === 'crouch' ? acts.crouch : (extAct ?? deadAct ?? acts.idle);
        base.setEffectiveWeight(1);
        if (deadStyle.pose === 'crouch') { base.time = base.getClip().duration * 0.5; }
        // 외부(UAL) 클립은 원하는 프레임에서 멈춘다 — 기본은 끝(쓰러져 멈춘 자세)
        if (extAct && base === extAct) { base.paused = true; base.time = Math.max(0, base.getClip().duration * (deadStyle.clipAt ?? 1) - 0.001); }
        mixer.update(0);
        model.updateMatrixWorld(true);
        if (deadStyle.curl) motion.curl(model);
        // **관절 포즈가 있으면 클립을 버리고 바인드 자세에서 관절 각도로 세운다.**
        // 한계 안에서만 돌아가므로 무릎이 옆으로 꺾이는 일이 없다
        if (deadStyle.joints && restQ && !deadStyle.clip) {
          curPose = deadStyle.joints;
          motion.applyPose(model, restQ, curPose);
          // 골반 뼈는 layFlat/roll 이 만진다 — 위에서 되돌린 바인드 회전 위에 다시 건다
        }
        holder.position.set(0, 0, 0);
        // **눕힌다.** orient() 가 걸어 둔 «세우는 회전» 이 누운 클립과 상쇄되어 시신이
        // 서 버린다 — 포즈를 적용한 뒤 다시 재서 수평으로 돌린다
        const root = deadStyle.joints?.__root ?? {};
        // 머리 방향은 __root.yaw 만큼 돌린다(기즈모 회전), 굴림은 __root.roll 이 있으면 그것
        const headDir = deadHeadDir && root.yaw ? deadHeadDir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), root.yaw) : deadHeadDir;
        if (headDir) motion.layFlat(holder, model, headDir);
        const rollAng = root.roll ?? deadStyle.roll;
        if (headDir && rollAng) motion.roll(model, headDir, rollAng);
        // **누운 몸은 발이 최저점이 아니다.** 스킨 전체의 최저점을 바닥에 맞춘다.
        // 누운 클립에는 Hips 이동 트랙이 없어서(측정) 골반이 서 있을 때 높이에 그대로
        // 남는다 — 이 보정을 안 하면 시신이 90cm 공중에 누워 있다
        const b = motion.skinBounds(model);
        if (!b.isEmpty()) {
          // **접지는 골반 기준이다. 최저점으로 맞추면 안 된다.**
          // 넘어진 자세는 팔이 뻗어 있어서 최저점이 «바닥에 닿은 손»이 된다 — 그것을 바닥에
          // 맞추면 몸통이 75cm 공중에 남는다(측정: Hips y 0.75, 뻗은 왼손 y 0.18).
          // 등을 대고 누운 몸의 골반 중심은 바닥에서 20cm 쯤이다. 손발이 바닥을 조금
          // 파고드는 것은 눈에 안 띄지만, 몸이 뜨는 것은 바로 보인다.
          /**
           * **월드에서 잰 차이를 로컬 좌표에 그대로 넣으면 안 된다.**
           *
           * `holder` 의 부모(그룹)는 사건 비율만큼 줄어 있다(초치성 0.77).
           * 그 안에서 `position.y` 를 1 올리면 월드로는 0.77 만 오른다 — 보정이
           * 매번 23% 모자라 시신이 마루에 조금씩 잠겼다.
           */
          const gs = holder.parent?.getWorldScale(new THREE.Vector3()).y || 1;
          const hipsBone = deadHeadDir ? motion.hipsOf(model) : null;
          if (hipsBone) {
            const hy = hipsBone.getWorldPosition(new THREE.Vector3()).y;
            holder.position.y -= (hy - (floorY + 0.22)) / gs;
          } else holder.position.y -= (b.min.y - (floorY + 0.01)) / gs;
          // **수평 중심을 원점에 맞춘다.** 누운 몸은 1.7m 를 차지하는데 골반이 원점에 있으면
          // 몸 전체가 한쪽으로만 뻗어 벽을 뚫는다 — 실제로 서쪽 벽(x -44) 안으로 0.6m
          // 들어가 있었다. 오프셋은 **그룹 프레임으로 변환해서** 넣는다(그룹이 rot 로
          // 돌아가 있으면 월드 X 로 밀면 어긋난다)
          holder.updateMatrixWorld(true);
          const b2 = motion.skinBounds(model);
          const gw = group.getWorldPosition(new THREE.Vector3());
          const gq = group.getWorldQuaternion(new THREE.Quaternion()).invert();
          const off = new THREE.Vector3((b2.min.x + b2.max.x) / 2 - gw.x, 0, (b2.min.z + b2.max.z) / 2 - gw.z);
          off.applyQuaternion(gq);
          holder.position.x -= off.x / gs;
          holder.position.z -= off.z / gs;
          // 기즈모로 옮긴 몸 전체 이동(그룹 프레임, m)
          holder.position.x += (root.x ?? 0) / gs; holder.position.y += (root.y ?? 0) / gs; holder.position.z += (root.z ?? 0) / gs;
          holder.updateMatrixWorld(true);
        }
      };
      if (deadStyle.clip) await setDeadClip(deadStyle.clip, deadStyle.clipAt);
      if (wantDead !== null) applyDead(wantDead);
      dbgReady = true;
      settle(true);
    })().catch((e) => {
      // **게임이 죽으면 안 된다.** 모델이 없으면 절차 아바타로 그냥 서 있는다
      console.warn('[아바타] 모델 로드 실패 — 절차 아바타로 남는다:', path, e);
      settle(false);
    });
  }

  const hq = new THREE.Quaternion();
  const gq = new THREE.Quaternion();
  const bq = new THREE.Quaternion();
  const nodAxis = new THREE.Vector3();
  const update = (dt: number, speed: number, pitch: number, crouch: boolean) => {
    // 굳은 시신은 아무것도 안 한다 — 포즈를 다시 섞으면 일어난다
    if (wantDead !== null) return;
    if (!mixer || !acts) {
      // 절차 아바타: 예전 그대로
      head.rotation.x = -pitch * 0.8;
      group.scale.y = crouch ? 0.68 : 1;
      return;
    }
    // 속도로 섞는다. 경계에서 딱 끊지 않고 겹치는 구간을 둬야 발이 미끄러지지 않는다
    const run = Math.min(1, Math.max(0, (speed - 2.0) / 1.4));
    const walk = Math.min(1, Math.max(0, (speed - 0.15) / 0.7)) * (1 - run);
    const still = Math.max(0, 1 - walk - run);
    const cw = crouch ? 1 : 0;
    acts.crouch.setEffectiveWeight(cw);
    acts.idle.setEffectiveWeight(still * (1 - cw));
    acts.walk.setEffectiveWeight(walk * (1 - cw));
    acts.run.setEffectiveWeight(run * (1 - cw));
    // 걸음 주기를 실제 속도에 맞춘다 — 안 맞으면 발이 땅에서 미끄러진다
    acts.walk.timeScale = Math.max(0.4, speed / 1.4);
    acts.run.timeScale = Math.max(0.6, speed / 3.4);
    mixer.update(dt);
    // **머리는 mixer 다음에 얹는다.** 대기 클립이 머리 뼈도 돌리므로, 먼저 넣으면 덮인다.
    //
    // 끄덕이는 축은 **그 사람의 오른쪽**이다. 처음에는 로드할 때 «월드 X축»으로 한 번
    // 계산해 두고 썼는데, 그러면 아바타가 어느 쪽을 보고 있느냐에 따라 결과가 달라진다 —
    // 옆을 보고 선 사람은 고개를 좌우로 까딱이고, 등을 돌린 사람은 반대로 끄덕였다.
    // 「왜 다 다르냐」는 것이 이 때문이었다. 몸이 매 프레임 돌므로 축도 매 프레임 구한다.
    if (headBone) {
      group.getWorldQuaternion(gq);
      headBone.getWorldQuaternion(bq);
      // 그룹(=몸)의 오른쪽을 월드로 꺼내서, 머리 뼈의 로컬 좌표로 가져온다
      nodAxis.set(1, 0, 0).applyQuaternion(gq).applyQuaternion(bq.invert()).normalize();
      hq.setFromAxisAngle(nodAxis, -pitch * 0.7);
      headBone.quaternion.multiply(hq);
    }
  };

  return { group, head, held, update, freezeDead,
    debug, setPose, getPose, setDeadClip, model: () => modelRef, rest: () => restQ, ready };
}

/** 이 사람을 보면 보이는 것. 아바타가 말 못 하는 나머지는 글로 뜬다 */
export function lookOf(castId: string | null): string {
  return (castId && CAST_BY_ID[castId]?.look) || '';
}
